import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import {
  ArrowLeft, Search, Loader2, FileDown, ListFilter, ChevronLeft, ChevronRight, MessageSquare,
} from 'lucide-react';

const clasColor = (c) => ({ promotor: '#16a34a', pasivo: '#f59e0b', detractor: '#dc2626' }[c] || '#94a3b8');
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const CLASIF = [
  { k: '__all__', l: 'Toda clasificación' },
  { k: 'promotor', l: 'Promotores' },
  { k: 'pasivo', l: 'Pasivos' },
  { k: 'detractor', l: 'Detractores' },
];

const ESTADOS = [
  { k: 'RESPONDIDA', l: 'Con respuesta' },
  { k: 'ENVIADA', l: 'Sin respuesta' },
  { k: 'todas', l: 'Todas' },
];

const estadoBadge = (e) => (e === 'RESPONDIDA'
  ? { label: 'Respondida', bg: '#dcfce7', fg: '#166534' }
  : { label: 'Enviada', bg: '#fef9c3', fg: '#854d0e' });

const HistorialEncuestasPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const themeColors = useThemeColors();

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [filtros, setFiltros] = useState({ proyectos: [], etapas: [], puntos: [] });

  const [puntoContacto, setPuntoContacto] = useState('');
  const [codigoProyecto, setCodigoProyecto] = useState('');
  const [codigoEtapa, setCodigoEtapa] = useState('');
  const [clasificacion, setClasificacion] = useState('');
  const [estado, setEstado] = useState('RESPONDIDA');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useAbortableLoad((signal) => {
    api.get('/experiencia/analytics/filtros', { signal }).then((r) => setFiltros(r.data)).catch(() => {});
  }, [api]);

  const buildParams = useCallback(() => {
    const p = { page, page_size: pageSize };
    if (puntoContacto) p.codigo_punto_contacto = puntoContacto;
    if (codigoProyecto) p.codigo_proyecto = codigoProyecto;
    if (codigoEtapa) p.codigo_etapa = codigoEtapa;
    if (clasificacion) p.clasificacion = clasificacion;
    if (estado) p.estado = estado;
    if (desde) p.desde = desde;
    if (hasta) p.hasta = `${hasta}T23:59:59`;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [page, puntoContacto, codigoProyecto, codigoEtapa, clasificacion, estado, desde, hasta, q]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await api.get('/experiencia/encuestas/historial', { params: buildParams(), signal });
      setData(res.data);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('Error al cargar el historial de encuestas');
    } finally {
      setLoading(false);
    }
  }, [api, buildParams]);

  // Debounce (reacciona a filtros, buscador y página)
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => load(controller.signal), 300);
    return () => { clearTimeout(t); controller.abort(); };
  }, [load]);

  // Al cambiar un filtro, volver a página 1
  useEffect(() => { setPage(1); }, [puntoContacto, codigoProyecto, codigoEtapa, clasificacion, estado, desde, hasta, q]);

  const limpiar = () => {
    setPuntoContacto(''); setCodigoProyecto(''); setCodigoEtapa(''); setClasificacion('');
    setEstado('RESPONDIDA'); setDesde(''); setHasta(''); setQ(''); setPage(1);
  };

  const exportar = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      delete params.page; delete params.page_size;
      const res = await api.get('/experiencia/encuestas/historial/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `historial_encuestas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Excel generado');
    } catch (e) {
      toast.error('Error al exportar a Excel');
    } finally {
      setExporting(false);
    }
  };

  const etapasDelProyecto = filtros.etapas.filter((e) => !codigoProyecto || e.codigo_proyecto === codigoProyecto);
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const rangoIni = data.total === 0 ? 0 : (data.page - 1) * pageSize + 1;
  const rangoFin = Math.min(data.page * pageSize, data.total);

  return (
    <div className="flex flex-col gap-5 animate-fadeIn" data-testid="historial-encuestas-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/experiencia/analytics')} data-testid="volver-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Historial de encuestas</h1>
          <p className="text-slate-500 text-sm">Todas las encuestas registradas (NPS / CSAT). Filtros combinables y exportación.</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Punto de contacto</label>
              <Select value={puntoContacto || '__all__'} onValueChange={(v) => setPuntoContacto(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-52" data-testid="filtro-punto-contacto"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los puntos</SelectItem>
                  {(filtros.puntos || []).map((p) => <SelectItem key={p.codigo} value={p.codigo}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Proyecto</label>
              <Select value={codigoProyecto || '__all__'} onValueChange={(v) => { setCodigoProyecto(v === '__all__' ? '' : v); setCodigoEtapa(''); }}>
                <SelectTrigger className="w-52" data-testid="filtro-proyecto"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los proyectos</SelectItem>
                  {filtros.proyectos.map((p) => <SelectItem key={p.codigo_proyecto} value={p.codigo_proyecto}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Etapa</label>
              <Select value={codigoEtapa || '__all__'} onValueChange={(v) => setCodigoEtapa(v === '__all__' ? '' : v)} disabled={!codigoProyecto}>
                <SelectTrigger className="w-44" data-testid="filtro-etapa"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las etapas</SelectItem>
                  {etapasDelProyecto.map((e) => <SelectItem key={e.codigo} value={e.codigo}>{e.nombre} ({e.codigo})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Clasificación NPS</label>
              <Select value={clasificacion || '__all__'} onValueChange={(v) => setClasificacion(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-48" data-testid="filtro-clasificacion"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASIF.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Estado</label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="w-40" data-testid="filtro-estado"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTADOS.map((e) => <SelectItem key={e.k} value={e.k}>{e.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Envío desde</label>
              <Input type="date" className="w-40" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="filtro-desde" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">Envío hasta</label>
              <Input type="date" className="w-40" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="filtro-hasta" />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
              <label className="text-xs text-slate-500">Cliente</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input className="pl-9" placeholder="Nombre, documento o correo…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filtro-cliente" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={limpiar} data-testid="limpiar-filtros-btn" className="text-slate-500">
              <ListFilter className="w-4 h-4 mr-1.5" /> Limpiar filtros
            </Button>
            <Button size="sm" className="text-white" onClick={exportar} disabled={exporting || data.total === 0} data-testid="exportar-excel-btn" {...getPrimaryButtonStyles(themeColors)}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Exportar a Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : data.items.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="historial-empty">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No se encontraron encuestas para estos filtros.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="[&_tbody_td]:py-1.5">
                <TableHeader>
                  <TableRow>
                    <TableHead>N° Ticket</TableHead>
                    <TableHead>Fecha de envío</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha de respuesta</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Celular</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead className="text-center">NPS</TableHead>
                    <TableHead>Clasificación</TableHead>
                    <TableHead className="text-center">CSAT</TableHead>
                    <TableHead>Punto de contacto</TableHead>
                    <TableHead>Proyecto</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Comentario</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((it) => {
                    const eb = estadoBadge(it.estado_encuesta);
                    return (
                    <TableRow key={it.id} data-testid={`historial-row-${it.id}`}>
                      <TableCell className="whitespace-nowrap font-mono text-xs text-slate-600">{it.codigo_ticket || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-slate-500 text-xs">{fmtDateTime(it.fecha_envio)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: eb.bg, color: eb.fg }}>
                          {eb.label}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-600">{fmtDate(it.fecha)}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-800 max-w-[200px] truncate">{it.cliente_nombre}</div>
                        {it.documento && <div className="text-xs text-slate-400">{it.documento}</div>}
                      </TableCell>
                      <TableCell className="text-slate-600 text-xs whitespace-nowrap">{it.celular || '—'}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{it.canal_atencion || '—'}</TableCell>
                      <TableCell className="text-center font-semibold" style={{ color: clasColor(it.clasificacion) }}>{it.nps ?? '—'}</TableCell>
                      <TableCell>
                        {it.clasificacion_label ? (
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: clasColor(it.clasificacion) }} />
                            {it.clasificacion_label}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-center text-slate-600">{it.csat ?? '—'}</TableCell>
                      <TableCell className="text-slate-600 max-w-[180px] truncate">{it.punto_nombre}</TableCell>
                      <TableCell className="text-slate-600 max-w-[160px] truncate">{it.proyecto_nombre}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{it.etapa_nombre || '—'}</TableCell>
                      <TableCell className="text-slate-500 text-xs max-w-[120px] truncate">{it.area_funcional || '—'}</TableCell>
                      <TableCell className="text-slate-500 text-xs max-w-[240px] truncate" title={it.comentario || ''}>{it.comentario || '—'}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginación */}
          {!loading && data.total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500" data-testid="paginacion-info">{rangoIni}–{rangoFin} de {data.total}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1} data-testid="pagina-anterior">
                  <ChevronLeft className="w-4 h-4" /> Anterior
                </Button>
                <span className="text-xs text-slate-500">Página {data.page} de {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages} data-testid="pagina-siguiente">
                  Siguiente <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistorialEncuestasPage;
